import { systemCode, xs_languageCode } from "..";
import { addOddsValuesToBet } from "./addOddsValuesToBet";
import { BET_IDS_TO_SHOW_WITHOUT_ADDITIONAL_INFO, HANDICAP_TYPES_TO_SORT_BY_DESC } from './constants';
import { decreaseOddsBy3Percent } from "./decreaseBy3Percent";
import { unmappedMarkets } from "./unmapped-markets";
import { byRanking } from './utils/byRanking';
import { getLiveAggs } from './utils/getLiveAggs';
import { getPrematchAggs } from './utils/getPrematchAggs';
import { handleAsianHandicapITDesc } from './utils/handleAsianHandicapITDesc';
import { isAsianHandicap } from './utils/isAsianHandicap';
import { parseHandicap } from './utils/parseHandicap';
import { setCustomizedOdds } from './utils/setCustomizedOdds';
import { getGenericDescWithPlaceholder } from './utils/getGenericDescWithPlaceholder';
import { specialHandling } from "./utils/specialHandling";

window.getLong = function(numb){
	return window.BigInt(numb || "0");
}

export function compactBets(bets, agg, compattaParametriche = false) {
    if (agg.compattate) {
        const betsByUniqueId = new Map();

        bets.forEach(bet => {
            if (bet.parametrica && !compattaParametriche) {
                return;
            }
            const uniqueId = `${bet.id}_${bet.handicap}`;
            if (!betsByUniqueId.has(uniqueId)) {
                betsByUniqueId.set(uniqueId, [bet]);
            } else {
                betsByUniqueId.get(uniqueId).push(bet);
            }
            
            if (bet.handicap != 0) {
                const uniqueId = `${bet.id}_0`;
                if (!betsByUniqueId.has(uniqueId)) {
                    betsByUniqueId.set(uniqueId, [bet]);
                } else {
                    betsByUniqueId.get(uniqueId).push(bet);
                }
            }
        });

        const compactedBets = [];
        Object.keys(agg.compattate).forEach((desc, i) => {
            const cmpIds = agg.compattate[desc];
            let cmpBet = null;

            let betsToCompact = [];
            cmpIds.forEach(cmpId => {
                if (betsByUniqueId.has(cmpId)) {
                    betsToCompact.push(...betsByUniqueId.get(cmpId));
                }
            });
            
            let hasOdds = false;
            betsToCompact.sort(byRanking).forEach(b => {
                if (cmpBet == null) {
                    const bet = betsToCompact[0];

                    const betId = `cmp_${agg.idAggregata}_${i + 1}`;
                    desc = agg.localeCmpDesc?.[desc]?.[xs_languageCode] ?? desc;
                    cmpBet = {
                        ...bet,
                        id: betId,
                        calculateHandicapDescription: false,
                        compactView: bet.parametrica,
                        desc,
                        isCompact: true,
                        odds: [],
                        originalBetsIds: [],
                        uniqueId: betId
                    };
                }
                if (!hasOdds && b.hasOdds === true) {
                    hasOdds = true;
                }
                b.skip = true;
                cmpBet.odds = cmpBet.odds.concat(b.odds);
                cmpBet.originalBetsIds.push(b.id);
            });
            
            if (cmpBet != null && cmpBet.odds.length > 0) {
                cmpBet.nOdds = cmpBet.odds.length;
                cmpBet.hasOdds = hasOdds;
                compactedBets.push(cmpBet);
            }
        });
        
        bets = bets.filter(b => b.skip == null).concat(compactedBets);
    }

    return bets;
}

export function getAggregateAllId(sportId, isLive){
	return (sportId * -1) - (isLive? 10000:0);
}

export function parseBetsData(matchData, aggregate, idAggregata, bets, isLive, compattaParametriche = false, skipPreprocessing = false) {
    if (!skipPreprocessing) {
        setCustomizedOdds(bets);
        if (isLive && systemCode === 'NETBET') {
            decreaseOddsBy3Percent(matchData, bets);
        }
    }
    
	matchData.isLive = isLive;
	const sportId = matchData.sportRef;
    const betsById = new Map(), gruppoById = {}, gruppiScommessa = [];

    // Creo un array con tutte i dati di tutte le aggregate
    let aggregata = null;
    aggregate.forEach(ag => {
        const agData = isLive ? getLiveAggs(sportId, ag) : getPrematchAggs(sportId, ag);
        if (agData != null) {
            if (ag === idAggregata) {
                aggregata = gruppoById[ag] = agData;
                gruppiScommessa.push(aggregata);
            } else {
                gruppiScommessa.push(gruppoById[ag] = agData);
            }
        }
    });

    gruppiScommessa.sort((a, b) => {
        if (a.idAggregata < 0) {
            return -1;
        } else if (b.idAggregata < 0) {
            return 1;
        }
        return a.ranking - b.ranking;
    });

    if (aggregata == null) {
        return {
            bets: [],
            betsById,
            gruppoById,
            gruppiScommessa
        };
    }

    let aggsToProcess = [];
    if (idAggregata < 0) {
        aggsToProcess = gruppiScommessa.filter(agg => agg.idAggregata > 0);

        // Assegna il ranking delle scommesse in base al ranking aggregata
        let rankingOffset = 0;
        gruppiScommessa.forEach(ag => {
            ag.scommesse.forEach(s => {
                s.ranking = s.ranking + rankingOffset;
                s.belongsMainGroup = ag.isPrincipale;
            });
            rankingOffset += ag.scommesse.length;
        });

        const unmappedMap = unmappedMarkets.getAll();
        if (unmappedMap.size > 0) {
            const unmappedBets = [];
            let rankingFake = 10000;
            bets.forEach(bet => {
                const id = `${sportId}_${isLive ? 'lv' : 'pr'}_${bet.cs}_${0}`;
                if (unmappedMap.has(id)) {
                    const bet = unmappedMarkets.getCopyOf(id);
                    bet.ranking = (rankingFake += 1);
                    unmappedBets.push(bet);
                }
            });

            aggsToProcess.push({
                id: idAggregata,
                scommesse: unmappedBets
            });
        }
    } else {
        aggsToProcess.push(aggregata);
    }

    const parsedBets = [];
    const parsedBetsSet = new Set();
    function pushBet(bet) {
        const uniqueId = `${bet.id}_${bet.handicap}`;
        if (!parsedBetsSet.has(uniqueId)) {
            parsedBetsSet.add(uniqueId);
            parsedBets.push(bet);
        }
    }
    
    aggsToProcess.forEach(agg => {
        const aggBets = agg.scommesse;
        let parsedAggBets = [];

        const betDataByUniqueId = new Map();
        const betsWithHandicapById = new Map();
        const betsWithoutOdds = new Set();
        bets.forEach(bet => {
            if (!betsWithHandicapById.has(bet.cs)) {
                betsWithHandicapById.set(bet.cs, [bet]);
            } else {
                betsWithHandicapById.get(bet.cs).push(bet);   
            }
            let uniqueId = `${bet.cs}_${bet.h}`;
            betDataByUniqueId.set(uniqueId, bet);
        });

        aggBets.forEach(bet => {
            const uniqueId = `${bet.id}_${bet.handicap}`;
            bet.eqs = bet.odds.map(o => {
                return {
                    ce: o.id,
                    q: o.value,
                    d: o.desc
                }
            })
            bet.h = bet.handicap;
            bet.cs = bet.id;
            bet.ia = bet.handicapInfo;
            if (!betsWithHandicapById.has(bet.id)) { // Se non presente tra le quotate, la aggiungo
                betsWithHandicapById.set(bet.id, [bet]);
                betsWithoutOdds.add(bet.id);
            }
            if (!betDataByUniqueId.has(uniqueId)) { // Se non presente tra le quotate, la aggiungo
                betDataByUniqueId.set(uniqueId, bet);
                betsWithoutOdds.add(uniqueId);
            }
        });
    
        const normal = [];
        const withHandicap = [];
        aggBets.forEach(bet => {
            const uniqueId = `${bet.id}_${bet.handicap}`;

            if ((bet.parametrica && betsWithoutOdds.has(bet.id)) || (!bet.parametrica && betsWithoutOdds.has(uniqueId))) {
                bet.hasOdds = false;
            } else {
                bet.hasOdds = true;
            }
            if (bet.parametrica) {
                withHandicap.push(bet);
            } else {
                normal.push(bet);
            }
        });
    
        normal.forEach(bet => {
            const updatedBet = {
                ...bet,
                uniqueId: `${matchData.uniqueId}_${bet.id}_${bet.handicap}`
            };
            
            // Se l'aggregata esiste tra quelle del torneo, aggiungi quote e handicap
            const uniqueId = `${updatedBet.id}_${updatedBet.handicap}`;
            if (betDataByUniqueId.has(uniqueId)) {
                const betData = betDataByUniqueId.get(uniqueId);
                updatedBet.handicap = betData.hs;
                updatedBet.handicapInfo = betData.ia;
                addOddsValuesToBet(matchData, betData, updatedBet, betData.eqs);
            }

            handleAsianHandicapITDesc(updatedBet);
    
            if (updatedBet.odds.length > 0) {
                parsedAggBets.push(updatedBet);
            }
        });
    
        withHandicap.forEach(hcpBet => {
            // Se l'aggregata esiste tra quelle del torneo, aggiungi quote e handicap
            if (betsWithHandicapById.has(hcpBet.id)) {
                const bets = betsWithHandicapById.get(hcpBet.id);
                bets.forEach(bet => {
					let desc = hcpBet.desc;
					//bet.h = bet.hs; 
					if(bet.hs !== undefined){
					// lo forzo ad usare l'handicap a stringa
						bet.h = bet.hs;
					}
                    /*if (hcpBet.tipoHandicap === 6 && hcpBet.aggDesc) {
                        desc = hcpBet.aggDesc;
                    } else if (!BET_IDS_TO_SHOW_WITHOUT_ADDITIONAL_INFO.includes(bet.cs)) {
                        desc = parseHandicap(hcpBet.desc, bet.h, hcpBet.tipoHandicap, bet.eqs, bet.dia, false, bet.ia);
                    }*/
                    if (!BET_IDS_TO_SHOW_WITHOUT_ADDITIONAL_INFO.includes(bet.cs)) {
                        desc = parseHandicap(hcpBet.desc, bet.h, hcpBet.tipoHandicap, bet.eqs, bet.dia, false, bet.ia);
                    }

                    const special = specialHandling(bet);
                    let handicapDesc = null;
                    if (special != null) {
                        handicapDesc = special;
                    } else {
                        handicapDesc = parseHandicap('', bet.h, hcpBet.tipoHandicap, bet.eqs, bet.dia, false, bet.ia, hcpBet.desc);
                    }
					
                    const updatedBet = {
                        ...hcpBet,
                        desc,
                        descNoHandicap: getGenericDescWithPlaceholder(hcpBet.desc),
                        uniqueId: `${matchData.uniqueId}_${bet.cs}_${bet.h}`,
                        handicap: bet.hs,
                        handicapDesc,
						handicapInfo: bet.ia,
                        dia: bet.dia,
                        s: bet.s
                    };
                    addOddsValuesToBet(matchData, bet, updatedBet, bet.eqs);
    
                    if (isAsianHandicap(updatedBet.id)) {
                        updatedBet.odds.forEach((odd, i) => {
                            let hcp = updatedBet.handicap;
                            let handleAsianHandicap = false;
                            if (i === 1) {
                                if (hcpBet.tipoHandicap !== 23) {
                                    hcp *= -1;
                                }
                                handleAsianHandicap = true;
                            }
                            odd.descNoHandicap = odd.desc; 
                            const special = specialHandling(bet);
                            let handicapDesc = null;
                            if (special != null) {
                                handicapDesc = special;
                            } else {
                                handicapDesc = parseHandicap('', hcp, hcpBet.tipoHandicap, bet.eqs, bet.dia, handleAsianHandicap, null);
                            }
                            odd.desc = `${odd.desc} (${handicapDesc})`;
                        });
                    }

                    if (updatedBet.odds.length > 0) {
                        parsedAggBets.push(updatedBet);
                    }
                });
            }
        });
        parsedAggBets = compactBets(parsedAggBets, agg, compattaParametriche);

        parsedAggBets.forEach(bet => pushBet(bet));
    });

    // Aggiungi parametriche alla mappa betsById
    parsedBets.forEach(bet => {
        if (bet.parametrica) {
            // Ordina esiti parametrica per handicap
            bet.odds.sort((odd1, odd2) => {
                return Number(window.getLong(odd1.betData.handicap) - window.getLong(odd2.betData.handicap));
            });

            if (!betsById.has(bet.id)) {
                betsById.set(bet.id, []);
            }
        
            const betsList = betsById.get(bet.id);
            const el = betsList.find(b => b.handicap == bet.handicap);
            if (el === undefined) {
                betsList.push(bet);
            }
        }
    });

    // Ordina le parametriche per handicap
    betsById.forEach(bets => {
        if (bets.length > 1 && bets[0].odds.length === 2) {
            bets.forEach(bet => {
                const odds = bet.odds;
                bet.diff = Math.abs(odds[0].rawValue - odds[1].rawValue);
            });
            bets = bets.sort((b1, b2) => b1.diff - b2.diff);
        } else if (bets.length > 1) {
            bets = bets.sort((a, b) => {
                if (a.tipoHandicap === b.tipoHandicap && HANDICAP_TYPES_TO_SORT_BY_DESC.includes(a.tipoHandicap)) {
                    return a.handicapDesc.localeCompare(b.handicapDesc);
                } else {
                    return Number(window.getLong(a.handicap) - window.getLong(b.handicap));
                }
            });
        }
    });

    // Ordina le scommesse per ranking
    // A parità di ranking ordinale per handicap
    aggregata.scommesse = parsedBets.sort((a, b) => {
        if (a.ranking === b.ranking) {
            return Number(window.getLong(a.handicap) - window.getLong(b.handicap));
        } else {
            return a.ranking - b.ranking;
        }
    });

    return {
        bets: parsedBets,
        betsById,
        gruppoById,
        gruppiScommessa
    };
}
